import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
}

export type CreateAllocationInput = { sku: string; qty: number; serial_code?: string | null };
export type CreateAllocationBatchInput = { items: CreateAllocationInput[] };

export type AllocationRecord = {
  id: number;
  order_id: number;
  sku: string;
  name: string;
  qty: number;
  serial_code?: string | null;
  status?: string | null;
  created_at?: string;
};

export type AllocationSerialBatchItem = {
  allocation_id: number;
  serial_code: string;
};

export const allocationsApi = createApi({
  reducerPath: 'allocationsApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Allocations', 'Orders'],
  endpoints: (builder) => ({
    // POST /orders/{id}/allocations
    createAllocation: builder.mutation<ApiResponseBase<any>, { orderId: number | string; body: CreateAllocationInput }>({
      query: ({ orderId, body }) => ({ url: `/v1/orders/${orderId}/allocations`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.Create'),
      invalidatesTags: (_res, _e, arg) => [{ type: 'Allocations', id: String(arg.orderId) }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear la reserva',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Allocations.Create'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.allocations.create',
              endpointName: e?.endpointName || 'Orders.Allocations.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /orders/{id}/allocations/serials
    createSerialAllocations: builder.mutation<
      ApiResponseBase<{ items: { allocation_id: number; status: string }[] }>,
      { orderId: number | string; body: CreateAllocationBatchInput }
    >({
      query: ({ orderId, body }) => ({ url: `/v1/orders/${orderId}/allocations/serials`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.CreateBatch'),
      invalidatesTags: (_res, _err, arg) => [{ type: 'Allocations', id: String(arg.orderId) }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear el lote de reservas',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Allocations.CreateBatch'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.allocations.createBatch',
              endpointName: e?.endpointName || 'Orders.Allocations.CreateBatch',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /orders/{id}/allocations
    listAllocations: builder.query<ApiResponseBase<any>, { orderId: number | string }>({
      query: ({ orderId }) => ({ url: `/v1/orders/${orderId}/allocations`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.List'),
      providesTags: (_r, _e, arg) => [{ type: 'Allocations', id: String(arg.orderId) }],
    }),

    // POST /orders/{id}/allocations/{alloc_id}/consume
    consumeAllocation: builder.mutation<
      ApiResponseBase<{ consumed: number }>,
      { orderId: number | string; allocId: number | string }
    >({
      query: ({ orderId, allocId }) => ({ url: `/v1/orders/${orderId}/allocations/${allocId}/consume`, method: 'POST' }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.Consume'),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Allocations', id: String(arg.orderId) }],
    }),

    // POST /orders/{id}/allocations/{alloc_id}/return
    returnAllocation: builder.mutation<
      ApiResponseBase<{ returned: number }>,
      { orderId: number | string; allocId: number | string }
    >({
      query: ({ orderId, allocId }) => ({ url: `/v1/orders/${orderId}/allocations/${allocId}/return`, method: 'POST' }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.Return'),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Allocations', id: String(arg.orderId) }],
    }),

    // POST /orders/{id}/allocations/serials/batch
    assignSerialsBatch: builder.mutation<
      ApiResponseBase<{ updated: number }>,
      { orderId: number | string; body: { items: AllocationSerialBatchItem[] } }
    >({
      query: ({ orderId, body }) => ({ url: `/v1/orders/${orderId}/allocations/serials/batch`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.SetSerialBatch'),
      // invalidatesTags: (_result, error) => (error ? [{ type: 'Orders' }] : [{ type: 'Orders' }]),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'No fue posible registrar los seriales de la reserva',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Allocations.SetSerialBatch'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.allocations.setSerial',
              endpointName: e?.endpointName || 'Orders.Allocations.SetSerialBatch',
              validationErrors:
                (e as any)?.details ?? (e as any)?.originalError?.errors ?? (e as any)?.validationErrors ?? {},
            }),
          );
        }
      },
    }),

    // POST /allocations/validate-serial
    validateSerial: builder.mutation<
      ApiResponseBase<{ available: boolean }>,
      { sku: string; serial_code: string }
    >({
      query: (body) => ({ url: '/v1/allocations/validate-serial', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.ValidateSerial'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al validar el serial',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Allocations.ValidateSerial'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.allocations.validateSerial',
              endpointName: e?.endpointName || 'Orders.Allocations.ValidateSerial',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PATCH /orders/{id}/allocations/{alloc_id}/serial
    updateAllocationSerial: builder.mutation<
      ApiResponseBase<{ status: string }>,
      { orderId: number | string; allocId: number | string; body: { serial_code: string } }
    >({
      query: ({ orderId, allocId, body }) => ({
        url: `/v1/orders/${orderId}/allocations/${allocId}/serial`,
        method: 'PATCH',
        body,
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Allocations.SetSerial'),
      invalidatesTags: (result, error, arg) => {
        if (error) return [{ type: 'Orders' }];
        return [
          // { type: 'Allocations', id: String(arg.orderId) },
          { type: 'Orders' },
        ];
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'No fue posible registrar el serial de la reserva',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Allocations.SetSerial'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.allocations.setSerial',
              endpointName: e?.endpointName || 'Orders.Allocations.SetSerial',
              validationErrors:
                (e as any)?.details ?? (e as any)?.originalError?.errors ?? (e as any)?.validationErrors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useCreateAllocationMutation,
  useCreateSerialAllocationsMutation,
  useListAllocationsQuery,
  useLazyListAllocationsQuery,
  useConsumeAllocationMutation,
  useReturnAllocationMutation,
  useAssignSerialsBatchMutation,
  useUpdateAllocationSerialMutation,
  useValidateSerialMutation,
} = allocationsApi;
